import { Icon } from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import { mdiChevronLeft, mdiChevronRight } from "@mdi/js";
import {
  Box,
  Button,
  Chip,
  List,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { config } from "../../../config";
import { useAuth } from "../../../hooks/useAuth";
import { useLeftMenu } from "../../../hooks/useLeftMenu";
import { useTopMenu } from "../../../hooks/useTopMenu";
import { AdministrationMenu } from "./menus/AdministrationMenu";
import { CallOffContractsMenu } from "./menus/CallOffContractsMenu";
import { DashboardsMenu } from "./menus/DashboardsMenu";
import { DeliverySchedulesMenu } from "./menus/DeliverySchedulesMenu";
import { DevelopersMenu } from "./menus/DevelopersMenu";
import { GoodsReceiptsMenu } from "./menus/GoodsReceiptsMenu";
import { HelpsMenu } from "./menus/HelpsMenu";
import { ManualOperationsMenu } from "./menus/ManualOperationsMenu";
import { MaterialManagementMenu } from "./menus/MaterialManagementMenu";
import { MaterialRequestFormsMenu } from "./menus/MaterialRequestFormsMenu";
import { MaterialSourcingMenu } from "./menus/MaterialSourcingMenu";
import { NegotiationsMenu } from "./menus/NegotiationsMenu";
import { PurchaseOrdersMenu } from "./menus/PurchaseOrdersMenu";
import { ServiceNegotiationsMenu } from "./menus/ServiceNegotiationsMenu";
import { ServiceSourcingMenu } from "./menus/ServiceSourcingMenu";
import { SpotPurchaseOrdersMenu } from "./menus/SpotPurchaseOrdersMenu";
import { SsuMenu } from "./menus/SsuMenu";
import { SummaryMenu } from "./menus/SummaryMenu";
import { TasksMenu } from "./menus/TasksMenu";
import { VendorsMenu } from "./menus/VendorsMenu";
import { Drawer, DrawerBottom } from "./styles";

export const LeftMenu: React.FC<{
  leftMenuOpen: boolean;
  setLeftMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({ leftMenuOpen, setLeftMenuOpen }) => {
  const { user } = useAuth();
  const theme = useTheme();
  const { appVersion } = config;
  const { topMenuHeight } = useTopMenu();

  const { setLeftMenuWidth } = useLeftMenu();
  const ref = useRef<HTMLDivElement>(null);

  const [openSubmenu, setOpenSubmenu] = useState<{
    [key: string | number]: boolean;
  }>({});
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const handleLeftMenu = useCallback(() => {
    setLeftMenuOpen((currentValue) => !currentValue);
  }, [setLeftMenuOpen]);

  const handleClick = useCallback(
    (e: string | number) => {
      setLeftMenuOpen(() => true); // Force menu expanded to show nested options
      setOpenSubmenu((currentValue) => ({ [e]: !currentValue[e] }));
    },
    [setLeftMenuOpen],
  );

  const getChevronIcon = useCallback(
    (status: boolean) => {
      if (status) {
        return theme.direction === "rtl" ? mdiChevronRight : mdiChevronLeft;
      }

      return theme.direction === "rtl" ? mdiChevronLeft : mdiChevronRight;
    },
    [theme.direction],
  );

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const resizeObserver = new ResizeObserver(() => {
      setLeftMenuWidth(element.offsetWidth);
    });

    resizeObserver.observe(element);

    setLeftMenuWidth(element.offsetWidth);

    return () => {
      resizeObserver.disconnect();
    };
  }, [setLeftMenuWidth]);

  useEffect(() => {
    if (isMobile) {
      setLeftMenuOpen(() => false);
    }
  }, [isMobile, setLeftMenuOpen]);

  useEffect(() => {
    if (!leftMenuOpen) {
      setOpenSubmenu(() => ({})); // Force all submenu close
    }
    window.leftMenuCollapsed(leftMenuOpen);
  }, [leftMenuOpen]);

  if (!user) {
    return null;
  }

  return (
    <Drawer
      open={leftMenuOpen}
      ref={ref}
      sx={{
        flexShrink: 0,
        zIndex: (currentTheme) => currentTheme.zIndex.appBar - 1,
      }}
      // variant={isMobile ? "temporary" : "permanent"}
      variant="permanent"
    >
      <Box
        sx={{
          marginTop: `${topMenuHeight}px`,
        }}
      >
        <List aria-labelledby="main-nav" component="nav">
          <DevelopersMenu
            handleClick={handleClick}
            leftMenuOpen={leftMenuOpen}
            user={user}
          />

          <AdministrationMenu
            handleClick={handleClick}
            leftMenuOpen={leftMenuOpen}
            openSubmenu={openSubmenu}
            user={user}
          />

          <SummaryMenu
            handleClick={handleClick}
            leftMenuOpen={leftMenuOpen}
            user={user}
          />

          <DashboardsMenu
            handleClick={handleClick}
            leftMenuOpen={leftMenuOpen}
            openSubmenu={openSubmenu}
            user={user}
          />

          <VendorsMenu
            handleClick={handleClick}
            leftMenuOpen={leftMenuOpen}
            openSubmenu={openSubmenu}
            user={user}
          />

          <SsuMenu
            handleClick={handleClick}
            leftMenuOpen={leftMenuOpen}
            openSubmenu={openSubmenu}
            user={user}
          />

          <MaterialSourcingMenu
            handleClick={handleClick}
            leftMenuOpen={leftMenuOpen}
            openSubmenu={openSubmenu}
            user={user}
          />

          <CallOffContractsMenu
            handleClick={handleClick}
            leftMenuOpen={leftMenuOpen}
            user={user}
          />

          <SpotPurchaseOrdersMenu
            handleClick={handleClick}
            leftMenuOpen={leftMenuOpen}
            user={user}
          />

          <NegotiationsMenu
            handleClick={handleClick}
            leftMenuOpen={leftMenuOpen}
            openSubmenu={openSubmenu}
            user={user}
          />

          <ServiceSourcingMenu
            handleClick={handleClick}
            leftMenuOpen={leftMenuOpen}
            openSubmenu={openSubmenu}
            user={user}
          />

          <ServiceNegotiationsMenu
            handleClick={handleClick}
            leftMenuOpen={leftMenuOpen}
            openSubmenu={openSubmenu}
            user={user}
          />

          <PurchaseOrdersMenu
            handleClick={handleClick}
            leftMenuOpen={leftMenuOpen}
            openSubmenu={openSubmenu}
            user={user}
          />

          <MaterialManagementMenu
            handleClick={handleClick}
            leftMenuOpen={leftMenuOpen}
            openSubmenu={openSubmenu}
            user={user}
          />

          <TasksMenu
            handleClick={handleClick}
            leftMenuOpen={leftMenuOpen}
            openSubmenu={openSubmenu}
            user={user}
          />

          <ManualOperationsMenu
            handleClick={handleClick}
            leftMenuOpen={leftMenuOpen}
            openSubmenu={openSubmenu}
            user={user}
          />

          <GoodsReceiptsMenu
            handleClick={handleClick}
            leftMenuOpen={leftMenuOpen}
            openSubmenu={openSubmenu}
            user={user}
          />

          <MaterialRequestFormsMenu
            handleClick={handleClick}
            leftMenuOpen={leftMenuOpen}
            user={user}
          />

          <DeliverySchedulesMenu
            handleClick={handleClick}
            leftMenuOpen={leftMenuOpen}
            user={user}
          />

          <HelpsMenu
            handleClick={handleClick}
            leftMenuOpen={leftMenuOpen}
            openSubmenu={openSubmenu}
            user={user}
          />
        </List>
      </Box>

      <DrawerBottom sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {!!leftMenuOpen && <Chip label={appVersion} variant="outlined" />}
        <Button
          color="energyRed"
          onClick={handleLeftMenu}
          size="large"
          sx={{ width: "100%" }}
          variant="contained"
        >
          <Icon icon={getChevronIcon(leftMenuOpen)} />
        </Button>
      </DrawerBottom>
    </Drawer>
  );
};
